<template>
  <a-modal
    :visible="visible"
    :title="isEdit ? $t('college.bulletin.config.modifierConfigSubject') : $t('scolarite.ajouterMatiere')"
    :footer="false"
    width="70%"
    @cancel="$emit('cancel')"
  >
    <a-form :form="form" @submit="handleSubmit">
      <a-form-item :label="$t('emploi.matiere')">
        <a-select
          :key="`subject-select-${getSubjectIdValue()}`"
          label-in-value
          :placeholder="$t('emploi.choisirMatiere')"
          :value="getSubjectSelectValue()"
          @change="handleSubjectChange"
          style="width: 100%"
          allowClear
          :disabled="isEdit"
          v-decorator="[
            'subject',
            {
                  rules: [
                    {
                      required: true,
                      message: $t('requis.matiere'),
                      validator: (rule, value, callback) => {
                        if (!value || (!value.key && !value)) {
                          callback($t('requis.matiere'));
                        } else {
                          callback();
                        }
                      }
                    },
                  ],
              initialValue: getSubjectSelectValue(),
            },
          ]"
        >
          <a-select-option
            v-for="subj in availableSubjects"
            :key="subj._id"
            :value="subj._id"
          >
            {{ subj.name }}
          </a-select-option>
        </a-select>
      </a-form-item>

      <a-form-item :label="$t('scolarite.coefficient')">
        <a-input-number
          v-decorator="[
            'coef',
            {
              rules: [{ required: true, message: $t('requis.coefficient') }],
              initialValue: subjectData.coef || 1,
            },
          ]"
          style="width: 100%"
          :min="0.1"
          :step="0.1"
        />
      </a-form-item>

      <!-- Mark Types Row -->
      <a-form-item :label="$t('college.bulletin.config.markTypes')">
        <div class="mark-types-container">
          <div
            v-for="markTypeKey in markTypesKeys"
            :key="markTypeKey"
            class="mark-type-card"
            :class="{ 'mark-type-enabled': hasMarkTypeData(markTypeKey) }"
          >
            <div class="mark-type-header">
              <a-checkbox
                :checked="getMarkTypeEnabled(markTypeKey)"
                @change="handleMarkTypeEnabledChange(markTypeKey, $event)"
                class="mark-type-checkbox"
              >
                <span class="mark-type-name">
                  {{ $t(`college.bulletin.config.markType.${markTypeKey}`) }}
                </span>
              </a-checkbox>
              <a-button
                type="link"
                icon="setting"
                size="small"
                @click="handleShowMarkTypeSettings(markTypeKey)"
                class="mark-type-settings-btn"
                :class="{ 'settings-visible': hasMarkTypeData(markTypeKey) }"
              />
            </div>
            <div
              v-if="hasMarkTypeData(markTypeKey)"
              class="mark-type-info"
            >
              <div class="mark-type-details">
                <span class="info-badge">
                  <span class="info-label">{{ $t('scolarite.coefficient') }}:</span>
                  <span class="info-value">{{ getMarkTypeCoef(markTypeKey) }}</span>
                </span>
                <span
                  v-if="getMarkTypeCount(markTypeKey) > 1"
                  class="info-badge"
                >
                  <span class="info-label">{{ $t('college.bulletin.config.count') }}:</span>
                  <span class="info-count">×{{ getMarkTypeCount(markTypeKey) }}</span>
                </span>
                <span
                  v-if="getSubsubjectName(markTypeKey)"
                  class="info-badge subsubject-badge"
                >
                  <a-icon type="tag" />
                  <span>{{ getSubsubjectName(markTypeKey) }}</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </a-form-item>

      <div class="form-actions mt-3">
        <a-button
          type="primary"
          htmlType="submit"
          :loading="loading"
          :disabled="loading || isReadOnly"
          class="mr-3"
        >
          {{ isEdit ? $t("action.enregistrer") : $t("action.ajouter") }}
        </a-button>
        <a-button key="back" @click="$emit('cancel')" :disabled="loading || isReadOnly">
          {{ $t("action.annuler") }}
        </a-button>
      </div>
    </a-form>
  </a-modal>
</template>

<script>
export default {
  name: "SubjectModal",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    subjectData: {
      type: Object,
      default: () => ({
        subject: null,
        coef: 1,
        order: 0,
        markTypes: {},
      }),
    },
    availableSubjects: {
      type: Array,
      required: true,
    },
    markTypesKeys: {
      type: Array,
      default: () => [],
    },
    subsubjects: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
    isReadOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      localSubjectData: {
        subject: null,
        coef: 1,
        order: 0,
        markTypes: {},
      },
    };
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.localSubjectData = JSON.parse(JSON.stringify(this.subjectData));
        if (!this.localSubjectData.markTypes) {
          this.localSubjectData.markTypes = this.createDefaultMarkTypes();
        }
        // Reset form fields to ensure fresh values
        this.$nextTick(() => {
          if (this.form) {
            this.form.resetFields();
            this.$forceUpdate();
          }
        });
      }
    },
    subjectData: {
      deep: true,
      handler(newVal, oldVal) {
        if (!this.visible) {
          this.localSubjectData = JSON.parse(JSON.stringify(newVal));
          if (!this.localSubjectData.markTypes) {
            this.localSubjectData.markTypes = this.createDefaultMarkTypes();
          }
        } else if (oldVal && newVal.markTypes) {
          Object.keys(newVal.markTypes).forEach(key => {
            if (!this.localSubjectData.markTypes) {
              this.localSubjectData.markTypes = {};
            }
            if (this.localSubjectData.markTypes[key]) {
              // Preserve enabled state, update other properties
              this.$set(this.localSubjectData.markTypes, key, {
                ...this.localSubjectData.markTypes[key],
                coef: newVal.markTypes[key].coef,
                count: newVal.markTypes[key].count,
                subsubject: newVal.markTypes[key].subsubject,
              });
            } else {
              // If it doesn't exist in localSubjectData, add it
              this.$set(this.localSubjectData.markTypes, key, { ...newVal.markTypes[key] });
            }
          });
        }
      },
    },
  },
  beforeMount() {
    this.form = this.$form.createForm(this, { name: "SubjectForm" });
  },
  methods: {
    getSubjectIdValue() {
      const subjectValue = this.localSubjectData.subject;
      if (!subjectValue) return 'none';
      if (typeof subjectValue === 'object') {
        return subjectValue._id || subjectValue.key || 'obj';
      }
      return String(subjectValue);
    },
    createDefaultMarkTypes() {
      const MARK_TYPES_KEYS = ["oral", "cont1", "cont2", "synth", "written", "tp"];
      return MARK_TYPES_KEYS.reduce((acc, key) => {
        if (key === "synth") {
          acc[key] = { enabled: true, coef: 2, count: 1, subsubject: null };
        } else {
          acc[key] = { enabled: false, coef: 1, count: 1, subsubject: null };
        }
        return acc;
      }, {});
    },
    handleSubmit(e) {
      if (e && e.preventDefault) {
        e.preventDefault();
      }
      this.form.validateFields((err, values) => {
        if (!err) {
          // Ensure subject is set from the form value
          const subjectId = values.subject ? (values.subject.key || values.subject) : this.localSubjectData.subject;
          if (!subjectId) {
            this.$message.error(this.$t('requis.matiere'));
            return;
          }
          const payload = {
            ...this.localSubjectData,
            ...values,
            subject: subjectId,
          };
          this.$emit("submit", payload);
        }
      });
    },
    getSubjectSelectValue() {
      if (!this.localSubjectData || !this.localSubjectData.subject) return undefined;

      const subjectValue = this.localSubjectData.subject;
      let subjectId;

      // Extract ID from different formats
      if (typeof subjectValue === 'object') {
        subjectId = subjectValue._id || subjectValue.key || subjectValue;
      } else {
        subjectId = subjectValue;
      }

      // Find the subject in available subjects
      const subject = this.availableSubjects.find(s => {
        const subjId = s._id || s;
        return String(subjId) === String(subjectId);
      });

      if (subject) {
        return { key: subject._id || subject, label: subject.name || '' };
      }

      return undefined;
    },
    handleSubjectChange(value) {
      if (value && typeof value === 'object' && value.key !== undefined) {
        this.localSubjectData.subject = value.key;
      } else if (value && (typeof value === 'string' || typeof value === 'number')) {
        this.localSubjectData.subject = value;
      } else {
        this.localSubjectData.subject = null;
      }
    },
    getMarkTypeEnabled(markTypeKey) {
      if (!this.localSubjectData.markTypes || !this.localSubjectData.markTypes[markTypeKey]) {
        return false;
      }
      return this.localSubjectData.markTypes[markTypeKey].enabled;
    },
    handleMarkTypeEnabledChange(markTypeKey, event) {
      if (!this.localSubjectData.markTypes) {
        this.localSubjectData.markTypes = this.createDefaultMarkTypes();
      }
      if (!this.localSubjectData.markTypes[markTypeKey]) {
        const defaultCoef = markTypeKey === 'synth' ? 2 : 1;
        this.$set(this.localSubjectData.markTypes, markTypeKey, {
          enabled: event.target.checked,
          coef: defaultCoef,
          count: 1,
          subsubject: null,
        });
      } else {
        this.$set(this.localSubjectData.markTypes[markTypeKey], 'enabled', event.target.checked);
      }
      this.$nextTick(() => {
        this.$forceUpdate();
      });
    },
    hasMarkTypeData(markTypeKey) {
      if (!this.localSubjectData || !this.localSubjectData.markTypes || !this.localSubjectData.markTypes[markTypeKey]) {
        return false;
      }
      return this.localSubjectData.markTypes[markTypeKey].enabled === true;
    },
    getMarkTypeCoef(markTypeKey) {
      if (!this.localSubjectData.markTypes || !this.localSubjectData.markTypes[markTypeKey]) {
        return markTypeKey === 'synth' ? 2 : 1;
      }
      return this.localSubjectData.markTypes[markTypeKey].coef || (markTypeKey === 'synth' ? 2 : 1);
    },
    getMarkTypeCount(markTypeKey) {
      if (!this.localSubjectData.markTypes || !this.localSubjectData.markTypes[markTypeKey]) {
        return 1;
      }
      return this.localSubjectData.markTypes[markTypeKey].count || 1;
    },
    getSubsubjectName(markTypeKey) {
      if (!this.localSubjectData.markTypes || !this.localSubjectData.markTypes[markTypeKey]) {
        return null;
      }
      const subsubjectId = this.localSubjectData.markTypes[markTypeKey].subsubject;
      if (!subsubjectId) {
        return null;
      }
      const subsubject = this.subsubjects.find(s => s._id === subsubjectId);
      return subsubject ? subsubject.name : null;
    },
    handleShowMarkTypeSettings(markTypeKey) {
      if (!this.localSubjectData.markTypes) {
        this.localSubjectData.markTypes = this.createDefaultMarkTypes();
      }
      if (!this.localSubjectData.markTypes[markTypeKey]) {
        const defaultCoef = markTypeKey === 'synth' ? 2 : 1;
        this.$set(this.localSubjectData.markTypes, markTypeKey, {
          enabled: false,
          coef: defaultCoef,
          count: 1,
          subsubject: null,
        });
      }
      this.$emit('show-mark-type-settings', markTypeKey, this.localSubjectData.markTypes[markTypeKey]);
    },
  },
};
</script>

<style scoped>
.mark-types-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-top: 8px;
}

.mark-type-card {
  border: 1.5px solid #e8e8e8;
  border-radius: 8px;
  padding: 12px;
  background-color: #ffffff;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.mark-type-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #e8e8e8 0%, #e8e8e8 100%);
  transition: all 0.3s ease;
}

.mark-type-card:hover {
  border-color: #d9d9d9;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
}

.mark-type-card.mark-type-enabled {
  border-color: #1890ff;
  background: linear-gradient(to bottom, #f0f9ff 0%, #ffffff 100%);
}

.mark-type-card.mark-type-enabled::before {
  background: linear-gradient(90deg, #1890ff 0%, #40a9ff 100%);
}

.mark-type-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  min-height: 32px;
}

.mark-type-checkbox {
  flex: 1;
}

.mark-type-checkbox >>> .ant-checkbox-wrapper {
  width: 100%;
  display: flex;
  align-items: center;
}

.mark-type-name {
  font-weight: 500;
  color: #262626;
  font-size: 14px;
  margin-left: 8px;
}

.mark-type-settings-btn {
  opacity: 0.4;
  transition: all 0.2s ease;
  color: #8c8c8c;
}

.mark-type-settings-btn:hover {
  opacity: 1;
  color: #1890ff;
}

.mark-type-settings-btn.settings-visible {
  opacity: 1;
  color: #1890ff;
}

.mark-type-info {
  margin-top: 6px;
  padding: 6px 8px;
  background-color: #f5f5f5;
  border-radius: 4px;
  border-top: 1px solid #e8e8e8;
  font-size: 11px;
}

.mark-type-details {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.info-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 6px;
  background-color: #ffffff;
  border: 1px solid #d9d9d9;
  border-radius: 3px;
  font-size: 11px;
  line-height: 1.4;
}

.info-label {
  color: #595959;
  font-weight: 500;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.info-value {
  color: #1890ff;
  font-weight: 600;
  font-size: 11px;
}

.info-count {
  color: #52c41a;
  font-weight: 600;
  font-size: 11px;
}

.subsubject-badge {
  color: #595959;
  border-color: #d9d9d9;
}

.subsubject-badge .anticon {
  font-size: 10px;
  color: #8c8c8c;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .mark-types-container {
    grid-template-columns: 1fr;
  }
}
</style>

